import {
  IMActivity,
  IMAssessment,
  IMAssessmentProblem,
  IMLesson,
  IMLessonAssessment,
} from '../types'
import { parseQuestion, parseQuestions } from './question'
import {
  ISimplifiedNode,
  ISkillsList,
} from '../../../components/ingestion/IngestedTree'
import { parseStandardsGroup } from './standards'
import { AssignmentType, NodeType } from 'shared/types/ingestion'
import { IM360Parser } from './IM360Parser'
import { respondingToStudentThinkingNodes } from './respondingToStudentThinking'
import { prepareQuestionsMultiQ } from './lessonActivities/questions/questions'

export function practiceProblemsAssessment({
  ordinal_title,
  practice_problems,
}: {
  ordinal_title: string
  practice_problems?: { practice_problems?: IMAssessmentProblem[] }
}) {
  if (!practice_problems?.practice_problems?.length) return []
  return [
    IM360Parser.getInstance().createAssessment(
      {
        title: `${ordinal_title} Practice Problems`,
        assignment: 'participation',
        assessment_grading: 'formative',
      },
      practice_problems?.practice_problems.flatMap(parseQuestion),
    ),
  ]
}

export function parseLessonAssessments({ assessments, activities }: IMLesson) {
  return assessments.reduce<ISimplifiedNode[]>(
    (acc, assessment, currentIndex) => {
      return [
        ...acc,
        ...lessonAssessmentActivity(assessment, currentIndex, activities),
      ]
    },
    [],
  )
}

function assessmentType(assessment: IMLessonAssessment): AssignmentType {
  if (assessment.slug === 'cooldown') {
    return 'exit_ticket'
  }
  return 'participation'
}

// This is the CoolDown assessment
function lessonAssessmentActivity(
  assessment: IMLessonAssessment,
  index: number,
  activities?: IMActivity[],
) {
  if (
    !assessment.print_component?.statement ||
    !assessment.print_component.student_response
  ) {
    return []
  }
  const parser = IM360Parser.getInstance()
  const skillsLists = parseStandardsGroup(
    assessment.standards,
    assessment.ancillary_content,
  )
  const addressing = skillsLists.filter(
    ({ data }) => data.title === 'Addressing',
  )
  const questions = prepareQuestionsMultiQ(assessment.print_component)
  const questionNodes: [ISkillsList] | undefined = addressing?.length
    ? [addressing[0]]
    : undefined
  questions.forEach((firstChild) => {
    if (firstChild.type === NodeType.Question) {
      firstChild.nodes = questionNodes
    } else if (firstChild.type === NodeType.Inline) {
      firstChild.nodes.forEach((secondChild) => {
        if (secondChild.type === NodeType.Question) {
          secondChild.nodes = questionNodes
        }
      })
    }
  })
  return [
    parser.createActivity(
      {
        title: parser.coolDownTitle(assessment, index, activities),
        assignment: assessmentType(assessment),
        assessment_grading: 'formative',
      },
      [
        ...skillsLists,
        ...getNarrative(assessment),
        ...getLaunch(assessment),
        parser.createNoteNode({
          title: assessment.type,
          value: `${assessment.time_estimate} minutes`,
          studentFacing: false,
        }),
        ...questions,
        ...respondingToStudentThinkingNodes(
          assessment.responding_to_student_thinking,
        ),
      ],
    ),
  ]
}

export function parseAssessment(
  assessment: IMAssessment,
  overrides?: {
    title?: string
    preNodes?: ISimplifiedNode[]
    assignment?: AssignmentType
    assessment_grading?: 'formative' | 'summative'
  },
) {
  const {
    preNodes = [],
    title = assessment.title,
    assignment = 'test',
    assessment_grading = 'formative',
  } = overrides ?? {}
  const isCheckReady = assessment.slug.includes('check-your-readiness')
  return IM360Parser.getInstance().createAssessment(
    {
      title,
      assignment: isCheckReady ? 'pre_test' : assignment,
      assessment_grading: isCheckReady ? 'formative' : assessment_grading,
    },
    [
      ...preNodes,
      ...getTeacherInstructions(assessment),
      ...parseQuestions(assessment),
    ],
  )
}

function getTeacherInstructions({ teacher_instructions }: IMAssessment) {
  if (!teacher_instructions?.trim()) return []
  return [
    IM360Parser.getInstance().createNoteNode({
      title: 'Teacher Instructions',
      value: teacher_instructions,
      studentFacing: false,
    }),
  ]
}

function getNarrative({ narrative }: IMLessonAssessment) {
  if (!narrative) return []
  return [
    IM360Parser.getInstance().createNoteNode({
      value: narrative,
      title: 'Narrative',
      studentFacing: false,
    }),
  ]
}

function getLaunch({ print_component }: IMLessonAssessment) {
  if (!print_component?.launch) return []
  return [
    IM360Parser.getInstance().createNoteNode({
      value: print_component.launch,
      title: 'Launch',
      studentFacing: false,
    }),
  ]
}
